/* GS://PROTOCOLO
 * Arquivo: src/pages/FinancialRecordsPage.js
 * Dossiê de Projeto e Manual de Colaboração: Ecossistema GoldenSisters
 * Versão: 3.5.1 – FIX: UI Dropdown e Limpeza de Linter
 * Impacto da Versão: [src/pages/FinancialRecordsPage.js]
 * Data: 18/01/2026
 * Responsável: Gemini (conforme Protocolo)
 *
 * Mudanças desta versão:
 * - [FIX]: Adicionado `text-gray-900 bg-white` no select de colaboradores para corrigir visibilidade dos nomes.
 * - [CLEAN]: Removido import não utilizado (Calculator) para limpar warning do console.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from '../firebase/config'; 
import { onAuthStateChanged } from 'firebase/auth';
import { collection, getDocs, query, where, Timestamp } from 'firebase/firestore'; 
import { 
    ArrowRightCircle, 
    TrendingUp, 
    Filter,
    ArrowUpRight,
    ArrowDownLeft,
    Printer,
    Users,
    CheckCircle
} from 'lucide-react';
import financialService from '../services/financialService'; 
import { FINANCIAL_TYPES, FINANCIAL_STATUS } from '../utils/constants';

const FinancialRecordsPage = ({ openModal }) => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1).toISOString().split('T')[0];
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().split('T')[0];
    
    const [startDate, setStartDate] = useState(firstDay);
    const [endDate, setEndDate] = useState(lastDay);
    const [transactions, setTransactions] = useState([]);
    const [summary, setSummary] = useState({ income: 0, expense: 0, balance: 0 });
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState(null);

    // Estados para Lançamento Manual
    const [formType, setFormType] = useState(FINANCIAL_TYPES.EXPENSE);
    const [amount, setAmount] = useState('');
    const [description, setDescription] = useState('');
    const [categoryName, setCategoryName] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // [GS-NEW]: Estados para Apuração de Colaboradores
    const [showApuracao, setShowApuracao] = useState(false);
    const [staffList, setStaffList] = useState([]);
    const [selectedStaffId, setSelectedStaffId] = useState('');
    const [apuracaoResult, setApuracaoResult] = useState(null);
    const [isCalculating, setIsCalculating] = useState(false);

    useEffect(() => {
        return onAuthStateChanged(auth, (u) => setUser(u));
    }, []);

    // Carrega lista de colaboradores para o dropdown
    useEffect(() => {
        const loadStaff = async () => {
            try {
                const q = query(collection(db, 'staff')); 
                const snap = await getDocs(q);
                const staff = snap.docs.map(d => ({ id: d.id, ...d.data() }));
                setStaffList(staff);
            } catch (error) {
                console.error("Erro ao carregar staff:", error);
            }
        };
        loadStaff();
    }, []);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const ledgerEntries = await financialService.getLedgerEntries({
                startDate,
                endDate
            });

            let inc = 0, exp = 0;
            
            ledgerEntries.forEach(item => {
                const st = (item.status || '').toLowerCase();
                const isPaid = st === 'paid' || st === 'pago' || item.status === FINANCIAL_STATUS.PAID;

                if (isPaid) {
                    const val = Number(item.amountNet) || 0;
                    if (item.type === FINANCIAL_TYPES.INCOME || item.type === 'income') inc += val;
                    else if (item.type === FINANCIAL_TYPES.EXPENSE || item.type === 'expense') exp += Math.abs(val);
                }
            });

            setTransactions(ledgerEntries);
            setSummary({ income: inc, expense: exp, balance: inc - exp });

        } catch (error) {
            console.error("Erro ao carregar extrato:", error);
        } finally {
            setLoading(false);
        }
    }, [startDate, endDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // [GS-NEW]: Lógica de Apuração
    const handleApurar = async () => {
        if (!selectedStaffId) return;
        setIsCalculating(true);
        setApuracaoResult(null);

        try {
            const staffMember = staffList.find(s => s.id === selectedStaffId);
            if (!staffMember) throw new Error("Colaborador não encontrado.");

            // Regras do Contrato
            const contract = staffMember.remuneration || { type: 'comissao', dailyRate: 0, commissionRate: 0 };
            
            // Buscar vendas do período
            const startD = new Date(startDate + 'T00:00:00');
            const endD = new Date(endDate + 'T23:59:59');
            
            const salesRef = collection(db, 'sales');
            const qSales = query(
                salesRef, 
                where('createdAt', '>=', Timestamp.fromDate(startD)),
                where('createdAt', '<=', Timestamp.fromDate(endD))
            );
            
            const salesSnap = await getDocs(qSales);
            let totalSales = 0;
            let daysWorked = new Set(); // Para contar diárias

            salesSnap.forEach(doc => {
                const sale = doc.data();
                const sellerId = sale.recordedBy?.uid || sale.userId;
                
                if (sellerId === selectedStaffId && sale.status !== 'cancelado') {
                    totalSales += Number(sale.total) || 0;
                    const saleDate = sale.createdAt?.toDate().toISOString().split('T')[0];
                    if (saleDate) daysWorked.add(saleDate);
                }
            });

            // Cálculos
            const commissionVal = (totalSales * (contract.commissionRate || 0)) / 100;
            
            let dailyVal = 0;
            const daysCount = daysWorked.size;
            if (contract.type === 'diaria' || contract.type === 'hibrido') {
                dailyVal = daysCount * (contract.dailyRate || 0);
            }

            const totalDue = commissionVal + dailyVal;

            setApuracaoResult({
                staffName: staffMember.name || staffMember.displayName,
                salesTotal: totalSales,
                daysWorked: daysCount,
                commissionDue: commissionVal,
                dailyDue: dailyVal,
                totalDue: totalDue,
                pixKey: contract.pixKey,
                contract
            });

        } catch (error) {
            console.error("Erro na apuração:", error);
            alert("Erro ao apurar: " + error.message);
        } finally {
            setIsCalculating(false);
        }
    };

    const handleGerarPagamento = async () => {
        if (!apuracaoResult || !user) return;
        if (!window.confirm(`Confirma o lançamento de R$ ${fmtMoney(apuracaoResult.totalDue)} para ${apuracaoResult.staffName}?`)) return;

        setIsSubmitting(true);
        try {
            const entryData = {
                type: FINANCIAL_TYPES.EXPENSE,
                amountGross: apuracaoResult.totalDue,
                amountNet: apuracaoResult.totalDue,
                description: `Pagamento - ${apuracaoResult.staffName} (Comissões/Diárias)`,
                category: { name: 'Salários e Comissões' },
                status: 'paid', // Já lança como pago (assumindo PIX imediato)
                dates: {
                    issue: new Date(),
                    payment: new Date()
                },
                origin: {
                    module: 'payroll',
                    recordedBy: user.uid,
                    details: `Vendas: ${fmtMoney(apuracaoResult.salesTotal)} | Dias: ${apuracaoResult.daysWorked}`
                }
            };

            await financialService.addEntry(entryData);
            setApuracaoResult(null);
            setSelectedStaffId('');
            fetchData(); // Recarrega extrato
            openModal(<div className="text-center p-4 text-green-600 font-bold">Pagamento registrado com sucesso!</div>, "Sucesso", "sm");

        } catch (error) {
            alert("Erro ao gerar pagamento: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleManualSubmit = async (e) => {
        e.preventDefault();
        if (!user) return;
        setIsSubmitting(true);

        try {
            const val = parseFloat(amount.replace(',', '.'));
            if (!val || val <= 0) throw new Error("Valor inválido");

            const entryData = {
                type: formType,
                amountGross: val,
                amountNet: val,
                description,
                category: { name: categoryName || 'Geral' }, 
                status: 'paid', // Manual entra como pago
                dates: {
                    issue: new Date(),
                    payment: new Date()
                },
                origin: {
                    module: 'manual_entry',
                    recordedBy: user.uid
                }
            };

            await financialService.addEntry(entryData);
            setAmount('');
            setDescription('');
            setCategoryName('');
            fetchData();
            openModal(<div className="text-center p-4 text-green-600 font-bold">Lançamento registrado no Ledger!</div>, "Sucesso", "sm");
        } catch (error) {
            alert("Erro: " + error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const fmtMoney = (v) => (v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    const fmtDate = (d) => d ? new Date(d).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-';

    const StatusBadge = ({ item }) => {
        const st = (item.status || '').toLowerCase();
        const isPaid = st === 'paid' || st === 'pago' || item.status === FINANCIAL_STATUS.PAID;
        
        return (
            <span className={`text-[10px] px-2 py-0.5 rounded-full border ${isPaid ? 'bg-green-100 text-green-700 border-green-200' : 'bg-yellow-100 text-yellow-700 border-yellow-200'}`}>
                {isPaid ? 'Pago' : 'Pendente'}
            </span>
        );
    };

    const onPrint = () => {
        window.print();
    };

    return (
        <>
            <div className="p-6 bg-gray-50 min-h-screen print:hidden">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                            <TrendingUp className="text-brand-gold" /> Extrato Financeiro 2.0
                        </h1>
                        <p className="text-sm text-gray-500">Ledger Unificado (Entradas, Saídas e Previsões)</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2 bg-white p-2 rounded-lg shadow-sm border border-gray-200">
                            <Filter className="w-4 h-4 text-gray-400" />
                            <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600"/>
                            <span className="text-gray-400">-</span>
                            <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="text-sm border-none focus:ring-0 text-gray-600"/>
                        </div>
                        <button onClick={onPrint} className="inline-flex items-center gap-2 bg-white border border-gray-300 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-50 shadow-sm transition-colors">
                            <Printer className="h-4 w-4" /> Imprimir
                        </button>
                    </div>
                </div>

                {/* Resumo */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-green-500">
                        <p className="text-gray-500 text-xs font-bold uppercase mb-1">Receitas (Realizadas)</p>
                        <h3 className="text-2xl font-bold text-green-600">{fmtMoney(summary.income)}</h3>
                    </div>
                    <div className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-red-500">
                        <p className="text-gray-500 text-xs font-bold uppercase mb-1">Despesas (Pagas)</p>
                        <h3 className="text-2xl font-bold text-red-600">{fmtMoney(summary.expense)}</h3>
                    </div>
                    <div className={`bg-white p-6 rounded-lg shadow-sm border-l-4 ${summary.balance >= 0 ? 'border-blue-500' : 'border-orange-500'}`}>
                        <p className="text-gray-500 text-xs font-bold uppercase mb-1">Saldo do Período</p>
                        <h3 className={`text-2xl font-bold ${summary.balance >= 0 ? 'text-blue-600' : 'text-orange-600'}`}>
                            {fmtMoney(summary.balance)}
                        </h3>
                    </div>
                </div>

                {/* [GS-NEW]: Módulo de Apuração (Acordeão) */}
                <div className="bg-white rounded-lg shadow-sm mb-8 border border-gray-200 overflow-hidden">
                    <button 
                        onClick={() => setShowApuracao(!showApuracao)}
                        className="w-full px-4 py-3 bg-gray-50 flex justify-between items-center text-sm font-bold text-gray-700 hover:bg-gray-100 transition"
                    >
                        <span className="flex items-center gap-2"><Users className="w-4 h-4 text-brand-gold"/> Apuração de Colaboradores (Comissões & Diárias)</span>
                        <span className="text-xs text-gray-400">{showApuracao ? 'Ocultar' : 'Expandir'}</span>
                    </button>
                    
                    {showApuracao && (
                        <div className="p-4 border-t border-gray-200 bg-white">
                            <div className="flex flex-col md:flex-row gap-4 items-end mb-4">
                                <div className="w-full md:w-64">
                                    <label className="block text-xs font-bold text-gray-500 mb-1">Colaborador</label>
                                    <select 
                                        className="w-full text-sm text-gray-900 bg-white border-gray-300 rounded-md focus:ring-brand-gold focus:border-brand-gold"
                                        value={selectedStaffId}
                                        onChange={(e) => setSelectedStaffId(e.target.value)}
                                    >
                                        <option value="">Selecione...</option>
                                        {staffList.map(s => (
                                            <option key={s.id} value={s.id}>{s.name || s.displayName} ({s.role})</option>
                                        ))}
                                    </select>
                                </div>
                                <button 
                                    onClick={handleApurar}
                                    disabled={!selectedStaffId || isCalculating}
                                    className="px-4 py-2 bg-blue-600 text-white font-bold rounded-md hover:bg-blue-700 transition text-sm disabled:opacity-50"
                                >
                                    {isCalculating ? 'Calculando...' : 'Apurar Período'}
                                </button>
                            </div>

                            {apuracaoResult && (
                                <div className="bg-blue-50 p-4 rounded-lg border border-blue-100 animate-fadeIn">
                                    <div className="flex justify-between items-start mb-4">
                                        <div>
                                            <h4 className="font-bold text-blue-900 text-lg">{apuracaoResult.staffName}</h4>
                                            <p className="text-xs text-blue-600">
                                                Contrato: {apuracaoResult.contract.type === 'hibrido' ? 'Híbrido' : apuracaoResult.contract.type === 'diaria' ? 'Diária Fixa' : 'Comissão'} 
                                                ({apuracaoResult.contract.commissionRate}% / {fmtMoney(apuracaoResult.contract.dailyRate)} dia)
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <span className="block text-xs text-gray-500">Total a Pagar</span>
                                            <span className="block text-xl font-bold text-blue-700">{fmtMoney(apuracaoResult.totalDue)}</span>
                                        </div>
                                    </div>
                                    
                                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                            <span className="block text-xs text-gray-500">Vendas Totais</span>
                                            <span className="font-bold">{fmtMoney(apuracaoResult.salesTotal)}</span>
                                        </div>
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                            <span className="block text-xs text-gray-500">Dias Trabalhados (Venda)</span>
                                            <span className="font-bold">{apuracaoResult.daysWorked} dias</span>
                                        </div>
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                            <span className="block text-xs text-gray-500">Comissão Calc.</span>
                                            <span className="font-bold">{fmtMoney(apuracaoResult.commissionDue)}</span>
                                        </div>
                                        <div className="bg-white p-2 rounded border border-blue-100">
                                            <span className="block text-xs text-gray-500">Diárias Calc.</span>
                                            <span className="font-bold">{fmtMoney(apuracaoResult.dailyDue)}</span>
                                        </div>
                                    </div>

                                    <div className="flex justify-between items-center pt-2 border-t border-blue-200">
                                        <span className="text-xs text-gray-500">
                                            PIX: <span className="font-mono bg-white px-1 rounded border border-gray-200">{apuracaoResult.pixKey || 'Não cadastrada'}</span>
                                        </span>
                                        <button 
                                            onClick={handleGerarPagamento}
                                            disabled={isSubmitting}
                                            className="px-4 py-2 bg-green-600 text-white font-bold rounded-md hover:bg-green-700 transition text-sm flex items-center gap-2"
                                        >
                                            <CheckCircle size={16}/> Gerar Pagamento
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Área de Lançamento Avulso */}
                <div className="bg-white p-4 rounded-lg shadow-sm mb-8 border border-gray-100">
                    <h4 className="text-sm font-bold text-gray-700 mb-3 flex items-center gap-2">
                        <ArrowRightCircle className="w-4 h-4 text-brand-gold" /> Lançamento Avulso (Caixa)
                    </h4>
                    <form onSubmit={handleManualSubmit} className="flex flex-col md:flex-row gap-3 items-end">
                        <div className="w-full md:w-32">
                            <select className="w-full text-sm border-gray-300 rounded-md focus:ring-brand-gold focus:border-brand-gold" value={formType} onChange={e => setFormType(e.target.value)}>
                                <option value={FINANCIAL_TYPES.EXPENSE}>Saída</option>
                                <option value={FINANCIAL_TYPES.INCOME}>Entrada</option>
                            </select>
                        </div>
                        <div className="w-full md:w-32">
                            <input type="number" step="0.01" placeholder="Valor" className="w-full text-sm border-gray-300 rounded-md focus:ring-brand-gold focus:border-brand-gold" value={amount} onChange={e => setAmount(e.target.value)} required />
                        </div>
                        <div className="flex-1 w-full">
                            <input type="text" placeholder="Descrição" className="w-full text-sm border-gray-300 rounded-md focus:ring-brand-gold focus:border-brand-gold" value={description} onChange={e => setDescription(e.target.value)} required />
                        </div>
                        <div className="w-full md:w-48">
                            <input type="text" placeholder="Categoria" className="w-full text-sm border-gray-300 rounded-md focus:ring-brand-gold focus:border-brand-gold" value={categoryName} onChange={e => setCategoryName(e.target.value)} />
                        </div>
                        <button type="submit" disabled={isSubmitting} className="px-4 py-2 bg-brand-gold text-brand-black font-bold rounded-md hover:bg-yellow-500 transition text-sm w-full md:w-auto">
                            {isSubmitting ? '...' : 'Lançar'}
                        </button>
                    </form>
                </div>

                {/* Tabela */}
                <div className="bg-white rounded-lg shadow overflow-hidden">
                    {loading ? (
                        <div className="p-12 text-center text-gray-400">Carregando ledger...</div>
                    ) : transactions.length === 0 ? (
                        <div className="p-12 text-center text-gray-400">Nenhum lançamento.</div>
                    ) : (
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-500 font-medium border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-3">Data</th>
                                    <th className="px-6 py-3">Descrição</th>
                                    <th className="px-6 py-3">Categoria</th>
                                    <th className="px-6 py-3 text-center">Status</th>
                                    <th className="px-6 py-3 text-right">Valor Líquido</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {transactions.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50 transition">
                                        <td className="px-6 py-3 text-gray-500 font-mono text-xs">{fmtDate(item.dateObj)}</td>
                                        <td className="px-6 py-3 font-medium text-gray-800">
                                            {item.description}
                                            {item.paymentBreakdown?.method && <span className="ml-2 text-xs text-gray-400 font-normal">({item.paymentBreakdown.method})</span>}
                                        </td>
                                        <td className="px-6 py-3 text-gray-500 text-xs uppercase">
                                            {typeof item.category === 'object' ? item.category?.name : item.category || 'Geral'}
                                        </td>
                                        <td className="px-6 py-3 text-center">
                                            <StatusBadge item={item} />
                                        </td>
                                        <td className={`px-6 py-3 text-right font-bold flex items-center justify-end gap-1 ${item.type === FINANCIAL_TYPES.INCOME ? 'text-green-600' : 'text-red-600'}`}>
                                            {item.type === FINANCIAL_TYPES.INCOME ? <ArrowUpRight size={14} /> : <ArrowDownLeft size={14} />}
                                            {fmtMoney(Math.abs(item.amountNet))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Layout Impressão */}
            <div className="hidden print:block print:absolute print:top-0 print:left-0 print:w-full h-auto bg-white p-8 z-[9999]">
                <div className="mb-6 border-b-2 border-black pb-4">
                    <h1 className="text-3xl font-bold uppercase tracking-wide">Golden Sisters</h1>
                    <h2 className="text-lg text-gray-600 mt-1">Extrato Financeiro Analítico</h2>
                    <div className="text-xs text-gray-500 mt-2">
                        Período: {new Date(startDate + 'T00:00:00').toLocaleDateString('pt-BR')} a {new Date(endDate + 'T00:00:00').toLocaleDateString('pt-BR')}
                    </div>
                </div>
                <div className="flex gap-8 mt-6 text-sm border-t border-gray-200 pt-3 mb-6">
                    <div><span className="block text-gray-500 text-xs uppercase">Receitas</span><span className="font-bold text-lg text-green-700">{fmtMoney(summary.income)}</span></div>
                    <div><span className="block text-gray-500 text-xs uppercase">Despesas</span><span className="font-bold text-lg text-red-700">{fmtMoney(summary.expense)}</span></div>
                    <div><span className="block text-gray-500 text-xs uppercase">Saldo</span><span className="font-bold text-lg text-black">{fmtMoney(summary.balance)}</span></div>
                </div>
                <table className="w-full text-xs text-left border-collapse">
                    <thead>
                        <tr className="border-b-2 border-black">
                            <th className="py-2 pr-2 font-bold uppercase w-24">Data</th>
                            <th className="py-2 pr-2 font-bold uppercase">Descrição</th>
                            <th className="py-2 pr-2 font-bold uppercase text-center">Status</th>
                            <th className="py-2 font-bold uppercase text-right w-24">Valor</th>
                        </tr>
                    </thead>
                    <tbody>
                        {transactions.map((item, idx) => {
                            const st = (item.status || '').toLowerCase();
                            const isPaid = st === 'paid' || st === 'pago' || item.status === FINANCIAL_STATUS.PAID;
                            return (
                                <tr key={item.id} className={`border-b border-gray-300 ${idx % 2 === 0 ? 'bg-gray-50 print:bg-transparent' : ''}`}>
                                    <td className="py-2 pr-2 align-top font-mono">{fmtDate(item.dateObj)}</td>
                                    <td className="py-2 pr-2 align-top"><span className="block font-semibold">{item.description}</span></td>
                                    <td className="py-2 pr-2 align-top text-center uppercase text-[10px]">
                                        {isPaid ? 'Pago' : 'Pendente'}
                                    </td>
                                    <td className="py-2 align-top text-right font-bold">{fmtMoney(Math.abs(item.amountNet))}</td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

export default FinancialRecordsPage;